import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Escape a literal for use inside SPARQL double-quoted strings. */
function escapeSparqlString(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Format a cell value for display: strip midnight timestamps, flatten objects. */
function formatCellValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') {
    return val.name || val.title || val.label || JSON.stringify(val);
  }
  const s = String(val);
  return s.replace(/T00:00:00(\.\d+)?Z?$/i, '');
}

/** Map API column names (Cypher aliases) to localized table headers. */
function sparqlColumnHeader(col, t) {
  const safe = String(col)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .toLowerCase();
  return t(`content:sparqlCol_${safe}`, { defaultValue: col });
}

const DEFAULT_QUERY = `SELECT ?node ?type ?label
WHERE {
  ?node <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> ?type .
  ?node <http://www.wikidata.org/entity/P1476> ?label .
}
LIMIT 50`;

function buildPredefinedQueries(t) {
  const bindPerson = escapeSparqlString(t('content:sparqlBindLabelPerson'));
  const bindGrave = escapeSparqlString(t('content:sparqlBindLabelGrave'));
  const bindDoc = escapeSparqlString(t('content:sparqlBindLabelDoc'));

  return [
    {
      id: 'main',
      name: t('content:sparqlPresetMainNodes'),
      query: DEFAULT_QUERY,
    },
    {
      id: 'people',
      name: t('content:sparqlPresetPeople'),
      query: `SELECT ?node ?label ?birth ?death
WHERE {
  ?node <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> <http://www.wikidata.org/entity/Q5> .
  ?node <http://www.wikidata.org/entity/P1476> ?label .
  OPTIONAL { ?node <http://www.wikidata.org/entity/P569> ?birth }
  OPTIONAL { ?node <http://www.wikidata.org/entity/P570> ?death }
}
ORDER BY ?label
LIMIT 30`,
    },
    {
      id: 'massGraves',
      name: t('content:sparqlPresetMassGraves'),
      query: `SELECT ?node ?label ?location
WHERE {
  ?node <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> <http://www.wikidata.org/entity/Q108163> .
  ?node <http://www.wikidata.org/entity/P1476> ?label .
  OPTIONAL { ?node <http://www.wikidata.org/entity/P131> ?location }
}
ORDER BY ?label
LIMIT 30`,
    },
    {
      id: 'statistics',
      name: t('content:sparqlPresetStatistics'),
      query: `SELECT ?type (COUNT(DISTINCT ?entity) AS ?count)
WHERE {
  {
    ?entity a <http://www.wikidata.org/entity/Q5> .
    BIND("${bindPerson}" AS ?type)
  } UNION {
    ?entity a <http://www.wikidata.org/entity/Q108163> .
    BIND("${bindGrave}" AS ?type)
  } UNION {
    ?entity a <http://www.wikidata.org/entity/Q49848> .
    BIND("${bindDoc}" AS ?type)
  }
}
GROUP BY ?type
ORDER BY ?type`,
    },
    {
      id: 'entityTypes',
      name: t('content:sparqlPresetEntityTypes'),
      query: `SELECT DISTINCT ?type (COUNT(?node) AS ?count)
WHERE {
  ?node <http://www.w3.org/1999/02/22-rdf-syntax-ns#type> ?type .
}
GROUP BY ?type
ORDER BY DESC(?count)
LIMIT 20`,
    },
  ];
}

export default function SPARQLQuery({ apiUrl }) {
  const { t, i18n } = useTranslation();
  const predefined = useMemo(() => buildPredefinedQueries(t), [t, i18n.language]);
  const [query, setQuery] = useState(DEFAULT_QUERY);
  const [results, setResults] = useState(null);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(null);
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef(null);

  const runQuery = async () => {
    if (!query.trim()) return;
    setIsLoading(true);
    setError(null);
    setResults(null);
    const start = performance.now();

    try {
      const data = await apiUrl.executeSparql(query.trim());
      const end = performance.now();
      setElapsed(Math.round(end - start));
      const bindings = Array.isArray(data?.results) ? data.results : [];
      const cols = data?.columns || (bindings.length > 0 ? Object.keys(bindings[0]) : []);
      setColumns(cols);
      setResults(bindings);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      runQuery();
    }
  };

  const copyJson = async () => {
    await navigator.clipboard.writeText(JSON.stringify(results, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <p id="sparql-section-hint" className="text-label text-ink-muted shrink-0">
        {t('content:queriesSectionIntro')}
      </p>
      <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0" style={{ minHeight: 0 }}>
      {/* Editor — full width on small screens, ~55% on large */}
      <div className="flex flex-col gap-4 min-w-0 lg:flex-[0_0_55%] lg:max-w-[55%]">
        {/* Predefined query pills */}
        <div className="flex flex-wrap gap-2">
          {predefined.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setQuery(p.query)}
              className="text-label text-ink-muted hover:text-accent-text border border-border-subtle hover:border-border-strong px-3 py-1 rounded transition-colors"
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Query textarea */}
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          rows={14}
          className="w-full bg-surface-raised border border-border-subtle rounded p-4 font-mono text-sm text-ink-secondary resize-none focus:outline-none focus:border-border-strong transition-colors"
          placeholder="SELECT ?s ?p ?o WHERE { ?s ?p ?o } LIMIT 10"
        />

        {/* Run button row */}
        <div className="flex items-center justify-between">
          <button
            onClick={runQuery}
            disabled={isLoading}
            aria-label={t('ui:run')}
            className="text-sm text-accent-text hover:text-accent-hover disabled:text-ink-muted transition-colors font-medium"
          >
            {isLoading ? t('ui:loading') : t('ui:run')}
          </button>
          <span className="text-label text-ink-muted">⌘ + Enter</span>
        </div>
      </div>

      {/* Results — right 45% */}
      <div className="flex flex-col gap-3 min-w-0 flex-1" role="region" aria-label={t('content:queryResults')}>
        {/* Metadata row */}
        {results !== null && !error && (
          <div className="flex items-center justify-between flex-shrink-0">
            <span className="text-label text-ink-muted">
              {results.length} {t('ui:rows')} · {elapsed}{t('ui:ms')}
            </span>
            <button
              onClick={copyJson}
              className="text-label text-ink-muted hover:text-accent-text transition-colors"
            >
              {copied ? t('ui:copied') : t('ui:copyJson')}
            </button>
          </div>
        )}

        {/* Error state */}
        {error && (
          <p className="text-sm text-signal-error">
            {t('content:errorPrefix')}: {error}
          </p>
        )}

        {/* Results table */}
        {results !== null && !error && (
          <div ref={resultsRef} className="overflow-auto border border-border-subtle rounded flex-1">
            {results.length === 0 ? (
              <p className="p-4 text-sm text-ink-muted">{t('content:noResults')}</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border-subtle bg-surface-raised">
                    {columns.map((col) => (
                      <th
                        key={col}
                        scope="col"
                        className="text-left text-label text-ink-muted uppercase tracking-widest px-4 py-2 font-medium whitespace-nowrap"
                      >
                        {sparqlColumnHeader(col, t)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, i) => (
                    <tr key={i} className="border-b border-border-subtle hover:bg-surface-raised transition-colors">
                      {columns.map((col) => {
                        const display = formatCellValue(row[col]);
                        return (
                          <td key={col} className="px-4 py-2 font-mono text-ink-secondary max-w-xs truncate" title={display}>
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Empty state (before first query) */}
        {results === null && !error && !isLoading && (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-ink-muted">{t('content:queryEditor')}</p>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
